import { X } from "lucide-react";
import { useEffect, useRef } from "react";

import { code128, qr } from "@/lib/codes";

/**
 * Код во весь экран: Code 128 для баркода товара, QR для кода места.
 *
 * Зачем это вообще: и баркод размера, и код места существуют, чтобы их
 * СКАНИРОВАЛИ. До сих пор карточка показывала их цифрами, то есть человеку у
 * стеллажа оставалось набирать тринадцать цифр руками — а сканер в руке при
 * этом уже был.
 *
 * Код рисуется ЧЁРНЫМ ПО БЕЛОМУ независимо от темы кабинета, и это не
 * недосмотр тёмной темы. Сканер читает отражение, а инвертированный код —
 * светлые полосы на тёмном — большинство ручных сканеров не берёт вовсе.
 * Поэтому подложка кода прибита цветом, а не токеном.
 *
 * Поля вокруг кода («тихая зона») — часть кода, а не отступ вёрстки: Code 128
 * требует десять пустых модулей по бокам, QR — четыре по кругу. Без них
 * сканер не находит границу и читает через раз.
 */

/** Что показываем: полосы или квадрат. */
export type CodeKind = "barcode" | "qr";

export type CodeRequest = {
  kind: CodeKind;
  value: string;
  /** Строка над кодом: «Баркод · размер 42 (S)», «Код места · короб 10902». */
  title: string;
};

const QUIET_BARCODE = 10;
const QUIET_QR = 4;

export function CodeSheet({
  request,
  onClose,
}: {
  request: CodeRequest;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Escape закрывает — иначе на телефоне без мыши окно становится ловушкой.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={request.title}
      data-annot="код"
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        // Клик по самому коду не закрывает окно: его наводят под сканер, и
        // случайное касание не должно убирать то, что человек сканирует.
        onClick={(e) => e.stopPropagation()}
        className="flex w-full max-w-[420px] flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-bg-elevated p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[13.5px] font-medium">{request.title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-[var(--radius-ui)] border border-border text-fg-muted hover:text-fg"
          >
            <X size={14} />
          </button>
        </div>

        <Plate request={request} />

        {/* Цифры под кодом остаются: сканер может не взять экран (блик,
            яркость, треснувшее стекло), и тогда единственный путь — набрать
            руками. Убрав их, мы бы отняли этот путь. */}
        <p className="tnum text-center text-[13px] text-fg-muted select-all">
          {request.value}
        </p>
      </div>
    </div>
  );
}

/** Белая подложка с кодом — или словами о том, почему кода нет. */
function Plate({ request }: { request: CodeRequest }) {
  const drawn =
    request.kind === "barcode" ? barcodeSvg(request.value) : qrSvg(request.value);

  if (!drawn) {
    return (
      <p className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
        Такой код нарисовать нельзя
      </p>
    );
  }
  return (
    <div className="rounded-[var(--radius-ui)] bg-white p-3">{drawn}</div>
  );
}

function barcodeSvg(value: string) {
  const got = code128(value);
  if (!got) return null;
  return (
    <svg
      // Растягиваем по ширине окна: чем шире полосы, тем с большего
      // расстояния берёт сканер. `preserveAspectRatio="none"` здесь безопасен
      // — Code 128 читается по ШИРИНЕ полос, высота на смысл не влияет.
      viewBox={`${-QUIET_BARCODE} 0 ${got.modules + QUIET_BARCODE * 2} 100`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Штрихкод Code 128: ${value}`}
      className="block h-[110px] w-full"
    >
      {got.bars.map((bar) => (
        <rect
          key={bar.x}
          x={bar.x}
          y={0}
          width={bar.width}
          height={100}
          fill="#000"
        />
      ))}
    </svg>
  );
}

function qrSvg(value: string) {
  const got = qr(value);
  if (!got) return null;
  const side = got.size + QUIET_QR * 2;
  return (
    <svg
      viewBox={`${-QUIET_QR} ${-QUIET_QR} ${side} ${side}`}
      role="img"
      aria-label={`QR-код: ${value}`}
      className="mx-auto block h-[240px] w-[240px] max-w-full"
      // Кратное увеличение без сглаживания: размытая граница модуля — это
      // ровно то, на чём сканер начинает промахиваться.
      shapeRendering="crispEdges"
    >
      <path d={got.path} fill="#000" />
    </svg>
  );
}
